import WebSocketClient from '@/includes/WebSocketClient';
import axios from 'axios';

export default {
	namespaced: true,
	state: {
		socket: null,
		connected: false,
		notifications: [],
		showNewNotification: false,
		adminOnlyGroups: [],
		groupsLoaded: false,
	},
	mutations: {
		SET_SOCKET(state, socket) {
			state.socket = socket;
		},
		SET_CONNECTED(state, status) {
			state.connected = status;
		},
		ADD_NOTIFICATION(state, notification) {
			state.notifications.unshift(notification);
		},
		SET_NOTIFICATIONS(state, notifications) {
			state.notifications = notifications;
		},
		SET_SHOW_NEW_NOTIFICATION(state, show) {
			state.showNewNotification = show;
		},
		DELETE_NOTIFICATION(state, code) {
			state.notifications = state.notifications.filter(n => n.code !== code);
		},
		MARK_AS_READ(state, code) {
			const notification = state.notifications.find(n => n.code === code);
			if (notification) {
				notification.read = true;
			}
		},
		MARK_ALL_AS_READ(state) {
			state.notifications = state.notifications.map(n => ({ ...n, read: true }));
		},
		DELETE_ALL_READ_NOTIFICATIONS(state) {
			state.notifications = state.notifications.filter(n => !n.read);
		},
		SET_ADMIN_ONLY_GROUPS(state, groups) {
			state.adminOnlyGroups = groups;
			state.groupsLoaded = true;
		}
	},
	actions: {
		init({ commit, state, rootState, dispatch }) {
			const isAdmin = rootState.userData?.is_admin || false;

			const loadNotifications = () => {
				axios.post('GetNotifications', { no_loader: true, data: {} }).then(res => {
					const all = res.data.body?.tables?.messages?.colvalues || [];
					const filtered = isAdmin ? all : all.filter(n => !state.adminOnlyGroups.includes(n.group));
					commit('SET_NOTIFICATIONS', filtered);
				});
			};

			if (isAdmin) {
				// Админ видит всё — загружаем уведомления сразу, без ожидания групп
				loadNotifications();
			} else {
				// Fail-closed: без групп не показываем уведомления обычному пользователю
				axios.post('GetNotificationsGroups', { no_loader: true, data: {} }).then(res => {
					if (res.data.body?.result != '0') return;
					const groups = res.data.body.tables?.groups?.colvalues || [];
					const adminOnly = groups.filter(g => g.admin_only).map(g => g.group);
					commit('SET_ADMIN_ONLY_GROUPS', adminOnly);
					loadNotifications();
				});
			}

			if (state.socket) {
				console.log('[Notifications] WebSocket уже инициализирован');
				return;
			}
			const socketToken = rootState.userData?.user_info_advanced?.socket_token;

			if (!socketToken) {
				console.error('[Notifications] Не найден socket_token');
				return;
			}

			const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
			const socket = new WebSocketClient({
				url: `${wsProtocol}//${window.location.host}/api/informator/ws?auth_token=${socketToken}`,
			});

			socket
				.onOpen(() => {
					commit('SET_CONNECTED', true);
				})
				.onError((error) => {
					console.log('error', error);
				})
				.onMessage((data) => {
					console.log('[Notifications] WebSocket message:', data);
					// Fast path: добавляем уведомление напрямую если WS-сообщение содержит полные данные
					// fail-closed: без загруженных групп не добавляем (могут быть admin_only)
					if (data && data.code && (isAdmin || (state.groupsLoaded && !state.adminOnlyGroups.includes(data.group)))) {
						const exists = state.notifications.some(n => n.code === data.code);
						if (!exists) {
							if (!data.hasOwnProperty('read')) {
								data.read = false;
							}
							commit('ADD_NOTIFICATION', data);
						}
					}
					// Перезапрашиваем с API — fallback для сигнальных WS-сообщений без данных
					dispatch('refreshNotifications');
					commit('SET_SHOW_NEW_NOTIFICATION', true);
				})
				.onClose(() => {
					commit('SET_CONNECTED', false);
				})
				.connect();

			commit('SET_SOCKET', socket);
		},
		refreshNotifications({ commit, state, rootState }) {
			const isAdmin = rootState.userData?.is_admin || false;
			if (!isAdmin && !state.groupsLoaded) return;
			axios.post('GetNotifications', { no_loader: true, data: {} }).then(res => {
				const all = res.data.body?.tables?.messages?.colvalues || [];
				const fresh = isAdmin ? all : all.filter(n => !state.adminOnlyGroups.includes(n.group));

				// Smart merge: сохраняем локально-прочитанные уведомления
				const localReadCodes = new Set(
					state.notifications.filter(n => n.read).map(n => n.code)
				);

				commit('SET_NOTIFICATIONS', fresh.map(n => ({
					...n,
					read: n.read || localReadCodes.has(n.code)
				})));
			});
		},
		close({ state, commit }) {
			if (state.socket) {
				state.socket.disconnect();
				commit('SET_SOCKET', null);
				commit('SET_CONNECTED', false);
			}
		},
		markAsRead({ commit }, code) {
			commit('MARK_AS_READ', code);
		},
		deleteNotification({ commit }, code) {
			commit('DELETE_NOTIFICATION', code);
		},
		markAllAsRead({ commit }) {
			commit('MARK_ALL_AS_READ');
		},
		deleteAllReadNotifications({ commit }) {
			commit('DELETE_ALL_READ_NOTIFICATIONS');
		},
	},
	getters: {
		isConnected: state => state.connected,
		notifications: state => state.notifications,
		unreadNotifications: state => state.notifications.filter(n => !n.read),
		readNotifications: state => state.notifications.filter(n => n.read),
		hasUnread: state => state.notifications.filter(n => !n.read).length > 0,
		unreadTodayCount: state => {
			const startOfDay = new Date();
			startOfDay.setHours(0, 0, 0, 0);
			const startTimestamp = startOfDay.getTime();
			const endTimestamp = startTimestamp + 24 * 60 * 60 * 1000;

			const toMillis = (ts) => (ts < 1e12 ? ts * 1000 : ts);

			return state.notifications.filter((notification) => {
				if (notification.read) {
					return false;
				}
				const createdAt = toMillis(notification.ts_create);
				return createdAt >= startTimestamp && createdAt < endTimestamp;
			}).length;
		}
	}
};